import { useEffect, useRef, useState } from 'react';
import { Row, Container } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setPreloader } from '../../redux/slices/preloader';
import DownloadCsv from '../../components/blocks/downloadCsv';
import Filters from '../../components/blocks/filters';
import useUrlParams from '../../hooks/useUrlParams';
import { setFilters } from '../../redux/slices/filters';
import MePageTitle from '../../components/blocks/title/mePageTitle';
import { setPdfContentRef } from '../../hooks/usePdfDownload';
import LinkClicksTab from './components/LinkClicksTab';
import MePageTabs, {
  LINK_CLICKS_TAB,
  PAGE_STATISTICS_TAB
} from './components/MePageTabs';
import PageStatisticsTab from './components/PageStatisticsTab';
import useResetFilters from '../../hooks/useResetFilters';
import LinkClicksAccessState from './components/LinkClicksAccessState';
import {
  LINK_CLICKS_ACCESS_STATUSES,
  setLinkClicksAccessStatus
} from '../../redux/slices/mePageData';
import useLinkClicksAccess from '../../hooks/useLinkClicksAccess';
import { LINK_CLICKS_EMPTY_STATES } from './components/LinkClicksTab/LinkClicksEmptyState';

const MePagePage = () => {
  const {
    dataLoad,
    isStatisticCollecting,
    statisticPath,
    linkClicksAccessStatus,
    filters
  } = useSelector((state) => ({
    dataLoad: state.preloader.dataLoad,
    isStatisticCollecting: state.meQrData.isStatisticCollecting,
    statisticPath: state.meQrData.statisticPath,
    linkClicksAccessStatus: state.mePageData.linkClicksAccessStatus,
    filters: state.filters
  }));

  const dispatch = useDispatch();
  const { id } = useUrlParams();
  const { getLinkClicksAccessStatus } = useLinkClicksAccess();
  const contentRef = useRef(null);
  const resetAllFilters = useResetFilters();
  const [activeTab, setActiveTab] = useState(PAGE_STATISTICS_TAB);
  const [linkClicksExportData, setLinkClicksExportData] = useState(null);
  const [hasSeenAnalytics, setHasSeenAnalytics] = useState(false);
  const selectedPageId = filters.qrIds.length === 1 ? filters.qrIds[0] : null;
  const selectedPageHasNoLinks =
    selectedPageId !== null &&
    (filters.selectedPageLinksCount === 0 ||
      filters.selectedPageLinksCount == null);
  const isLinkClicksAccessRestricted =
    activeTab === LINK_CLICKS_TAB &&
    linkClicksAccessStatus !== null &&
    linkClicksAccessStatus !== LINK_CLICKS_ACCESS_STATUSES.AVAILABLE;

  const isLinkClicksAccessLoading =
    activeTab === LINK_CLICKS_TAB && linkClicksAccessStatus === null;

  useEffect(() => {
    setPdfContentRef(contentRef);
    return () => setPdfContentRef(null);
  }, []);

  useEffect(() => {
    dispatch(setPreloader(Object.values(dataLoad).some((item) => item)));
  }, [dispatch, dataLoad]);

  useEffect(() => {
    let isCancelled = false;

    dispatch(setLinkClicksAccessStatus(null));

    getLinkClicksAccessStatus().then((status) => {
      if (!isCancelled) {
        dispatch(setLinkClicksAccessStatus(status));
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [dispatch, getLinkClicksAccessStatus]);

  useEffect(() => {
    setHasSeenAnalytics(false);
  }, [selectedPageId, activeTab]);

  useEffect(() => {
    if (id) {
      dispatch(
        setFilters({
          ...filters,
          qrIds: [Number(id)],
          mode: 'period',
          isFitersActive: true
        })
      );
    }
    // eslint-disable-next-line
  }, [id]);

  if (id && filters.qrIds.length === 0) {
    return null;
  }

  return (
    <>
      {activeTab === PAGE_STATISTICS_TAB &&
        (statisticPath || isStatisticCollecting) && <DownloadCsv />}
      <Container ref={contentRef}>
        <Row className="g-4 mb-5">
          <div data-pdf-exclude>
            <MePageTitle />
            <MePageTabs activeTab={activeTab} onTabChange={setActiveTab} />
          </div>
          {!isLinkClicksAccessRestricted && !isLinkClicksAccessLoading && (
            <div data-pdf-exclude>
              <Filters
                isLinkClicksTab={activeTab === LINK_CLICKS_TAB}
                linkClicks={linkClicksExportData?.links ?? []}
              />
            </div>
          )}
          {isLinkClicksAccessLoading ? null : isLinkClicksAccessRestricted ? (
            <LinkClicksAccessState
              status={linkClicksAccessStatus}
              onBackToStatistics={() => setActiveTab(PAGE_STATISTICS_TAB)}
            />
          ) : activeTab === LINK_CLICKS_TAB ? (
            <LinkClicksTab
              selectedPageId={selectedPageId}
              emptyState={
                selectedPageHasNoLinks
                  ? LINK_CLICKS_EMPTY_STATES.NO_LINKS
                  : undefined
              }
              isFiltersActive={filters.isFitersActive}
              onResetFilters={resetAllFilters}
              hasSeenAnalytics={hasSeenAnalytics}
              onDataChange={(data) => {
                setLinkClicksExportData(data);
                if (data?.links.length > 0) setHasSeenAnalytics(true);
              }}
            />
          ) : (
            <PageStatisticsTab />
          )}
        </Row>
      </Container>
    </>
  );
};

export default MePagePage;
